import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sparkle, PencilSimpleLine, FloppyDisk, X, Plus, ArrowUpRight, CheckCircle, Circle } from "@phosphor-icons/react";

const PLAYBOOK_LABEL = {
  trusted_advisor: "Trusted Advisor",
  strategic_initiative: "Strategic Initiative",
  executive_convening: "Executive Convening",
  executive_communication: "Executive Comms",
};

const STATUS_OPTIONS = [
  { key: "not_started", label: "Not started" },
  { key: "in_progress", label: "In progress" },
  { key: "complete", label: "Complete" },
];

/**
 * Drawer that opens when a partner clicks a phase node on the arcs.
 *
 * Modes:
 *   • view (default): read-only, shows intent, activities, deliverables, questions.
 *   • edit  (Method page): full editor for firm-wide canonical copy.
 *   • track (Engagement page): partner marks status + writes engagement notes,
 *                              cannot edit the canonical copy itself.
 */
export default function MethodPhaseDrawer({
  open,
  onOpenChange,
  phase,        // full phase object (client_label, framework_label, mindset, intent, ...)
  mode = "view",  // 'view' | 'edit' | 'track'
  onSave,       // (updatedPhase) => Promise; only used in edit / track
}) {
  const [draft, setDraft] = useState(phase || null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { setDraft(phase || null); }, [phase]);

  if (!phase || !draft) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setList = (k, i, v) => setDraft((d) => {
    const list = [...(d[k] || [])];
    list[i] = v;
    return { ...d, [k]: list };
  });
  const addToList = (k) => setDraft((d) => ({ ...d, [k]: [...(d[k] || []), ""] }));
  const removeFromList = (k, i) => setDraft((d) => ({ ...d, [k]: (d[k] || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      // Strip empty strings on save so the read view stays clean.
      const cleaned = {
        ...draft,
        activities: (draft.activities || []).map((s) => s.trim()).filter(Boolean),
        deliverables: (draft.deliverables || []).map((s) => s.trim()).filter(Boolean),
        questions: (draft.questions || []).map((s) => s.trim()).filter(Boolean),
      };
      await onSave(cleaned);
      toast.success("Phase saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const askYadidAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/method/template/phase/${phase.key}/ai`);
      setDraft(data);
      toast.success(`YadidAi drafted a fresh ${phase.client_label} phase — review before saving`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setAiBusy(false); }
  };

  const editable = mode === "edit";
  const track = mode === "track";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto rounded-none bg-card p-0 border-l border-primary/30" data-testid="method-phase-drawer">
        <SheetHeader className="p-6 border-b border-border">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary flex items-center gap-2">
            <span>{phase.framework_label}</span>
            <span className="text-muted-foreground">·</span>
            <span className="italic">{phase.mindset}</span>
          </div>
          <SheetTitle className="font-display text-3xl font-bold tracking-tight text-foreground mt-1" style={{ fontFamily: "Georgia, serif" }}>
            {phase.client_label}
          </SheetTitle>
          {editable ? (
            <Textarea
              rows={2}
              value={draft.intent || ""}
              onChange={(e) => set("intent", e.target.value)}
              className="rounded-none bg-secondary/40 mt-3 italic"
              style={{ fontFamily: "Georgia, serif" }}
              data-testid="phase-intent-input"
            />
          ) : (
            <p className="mt-3 italic text-base leading-relaxed text-muted-foreground" style={{ fontFamily: "Georgia, serif" }}>
              {draft.intent}
            </p>
          )}
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-2">
            Typical duration · {editable ? (
              <Input
                value={draft.duration || ""}
                onChange={(e) => set("duration", e.target.value)}
                className="rounded-none bg-secondary/40 mt-1 h-7"
                data-testid="phase-duration-input"
              />
            ) : (
              <span className="text-foreground normal-case tracking-normal font-serif italic">{draft.duration}</span>
            )}
          </div>
        </SheetHeader>

        <div className="p-6 space-y-8">
          {/* AI + edit toolbar */}
          {editable && (
            <div className="flex flex-wrap gap-2 -mt-2">
              <Button
                onClick={askYadidAi}
                disabled={aiBusy}
                variant="outline"
                className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
                data-testid="phase-ai-draft-btn"
              >
                <Sparkle size={14} weight="fill" className="mr-1.5" />
                {aiBusy ? "Drafting…" : "Ask YadidAi to redraft this phase"}
              </Button>
            </div>
          )}

          {/* Engagement-only: status + notes */}
          {track && (
            <section className="border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="phase-track-panel">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">This engagement's arc</div>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => set("status", opt.key)}
                    data-testid={`phase-status-${opt.key}`}
                    className={`text-[0.625rem] font-mono uppercase tracking-widest px-3 py-1.5 border transition ${
                      draft.status === opt.key
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border/60 text-muted-foreground hover:border-primary hover:text-primary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Notes for this client (private)</Label>
                <Textarea
                  rows={4}
                  value={draft.notes || ""}
                  onChange={(e) => set("notes", e.target.value)}
                  className="rounded-none bg-secondary/40 mt-1 font-serif"
                  placeholder="What's specific to this engagement in this phase — surfaces, tensions, unspoken moments…"
                  data-testid="phase-notes-input"
                />
              </div>
            </section>
          )}

          {/* Activities */}
          <PhaseList
            testid="phase-activities"
            label="What we do"
            items={draft.activities}
            editable={editable}
            onChange={(i, v) => setList("activities", i, v)}
            onAdd={() => addToList("activities")}
            onRemove={(i) => removeFromList("activities", i)}
          />
          {/* Deliverables */}
          <PhaseList
            testid="phase-deliverables"
            label="What leaves the room"
            items={draft.deliverables}
            editable={editable}
            onChange={(i, v) => setList("deliverables", i, v)}
            onAdd={() => addToList("deliverables")}
            onRemove={(i) => removeFromList("deliverables", i)}
          />
          {/* Questions */}
          <PhaseList
            testid="phase-questions"
            label="Questions we hold"
            items={draft.questions}
            editable={editable}
            onChange={(i, v) => setList("questions", i, v)}
            onAdd={() => addToList("questions")}
            onRemove={(i) => removeFromList("questions", i)}
            italicRead
          />

          {/* Linked playbooks */}
          <section>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
              <ArrowUpRight size={11} weight="bold" /> Where this phase lands hardest
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLAYBOOK_LABEL).map(([key, label]) => {
                const on = (draft.linked_playbook_types || []).includes(key);
                if (!editable) {
                  return on ? (
                    <span key={key} className="text-[0.6875rem] font-mono uppercase tracking-widest border border-primary/60 text-primary px-2 py-1">
                      {label}
                    </span>
                  ) : null;
                }
                return (
                  <button
                    key={key}
                    onClick={() => {
                      const next = new Set(draft.linked_playbook_types || []);
                      on ? next.delete(key) : next.add(key);
                      set("linked_playbook_types", Array.from(next));
                    }}
                    data-testid={`phase-playbook-${key}`}
                    className={`text-[0.6875rem] font-mono uppercase tracking-widest px-2 py-1 border transition ${
                      on ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:border-primary hover:text-primary"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        {(editable || track) && (
          <div className="p-6 border-t border-border sticky bottom-0 bg-card flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="phase-save-btn"
            >
              <FloppyDisk size={14} weight="bold" className="mr-1.5" />
              {saving ? "Saving…" : "Save phase"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


function PhaseList({ testid, label, items, editable, onChange, onAdd, onRemove, italicRead = false }) {
  return (
    <section data-testid={testid}>
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">{label}</div>
      <ul className="space-y-2">
        {(items || []).map((val, i) => (
          <li key={i} className="flex items-start gap-2 group">
            <span className="text-primary shrink-0 mt-2">·</span>
            {editable ? (
              <>
                <Input
                  value={val}
                  onChange={(e) => onChange(i, e.target.value)}
                  className="rounded-none bg-secondary/40 flex-1"
                  data-testid={`${testid}-input-${i}`}
                />
                <button
                  onClick={() => onRemove(i)}
                  className="opacity-40 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1"
                  aria-label="Remove"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <span
                className={`text-sm leading-relaxed ${italicRead ? "italic text-muted-foreground" : "text-foreground"}`}
                style={italicRead ? { fontFamily: "Georgia, serif" } : {}}
              >
                {val}
              </span>
            )}
          </li>
        ))}
        {(items || []).length === 0 && !editable && (
          <li className="text-xs italic text-muted-foreground/70">(empty)</li>
        )}
      </ul>
      {editable && (
        <button
          onClick={onAdd}
          className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-primary hover:opacity-80 inline-flex items-center gap-1"
          data-testid={`${testid}-add`}
        >
          <Plus size={11} weight="bold" /> Add
        </button>
      )}
    </section>
  );
}
